import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PmsMainPageContainer,
  Content,
  NoDataWrapper,
  NoDataTitle,
  NoDataDescription,
  TableContainer,
  LoaderWrapper,
  NameWrapper,
} from './PmsMainPage.styled';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import Header from '../../components/Header';
import FilterBar from '../../components/FilterBar/FilterBar';
import Footer from '../../components/Footer';
import { Table, CardViewX, Dropdown, Alert, Icon as AnnIcon, Typography} from '@ann/web-components';
import Icon from '../../../../../elements/icon/Icon';
import PromptShareModal from '../../components/PromptShareModal';
import PromptArchiveModal from "../../components/PromptArchiveModal/PromptArchiveModal";
import PromptDuplicateModal from "../../components/PromptDuplicateModal/PromptDuplicateModal";
import { CircularProgress } from '@mui/material';
import PreviewSlideOut from '../../components/PreviewSlideOut';
import { useStore } from "../../../../../hooks";
import { listPrompts, getPromptById, createPrompt } from '../../../../../services';

const PmsMainPage = () => {
  const { authStore } = useStore();
  const tenantId = authStore.currentWorkspaceUuid
  const navigate = useNavigate();
  const [mode, setMode] = useState('card');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [prompts, setPrompts] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [isSlideoutOpen, setIsSlideoutOpen] = useState(false);
  const [selectedPromptForSlideout, setSelectedPromptForSlideout] = useState(null);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [promptToArchive, setPromptToArchive] = useState(null);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [promptToDuplicate, setPromptToDuplicate] = useState(null);
  const [promptIdToDuplicate, setPromptIdToDuplicate] = useState(null);
  const [alert, setAlert] = useState(null);

  const getMenuItems = (rowData) => [
    {
      id: 1, label: 'Edit', onClick: () => navigate(`/v2/pms/config/${rowData.uuid}`)
     },
    {
      id: 2,
      label: "Duplicate",
      onClick: () => {
        setPromptToDuplicate(rowData);
        setPromptIdToDuplicate(rowData.uuid);
        setIsDuplicateModalOpen(true);
      },
    },
    {
      id: 3,
      label: "Archive",
      onClick: () => {
        setPromptToArchive(rowData);
        setIsArchiveModalOpen(true);
      },
    },
    //{ id: 4, label: 'Delete',    onClick: () =>{} },
    {
      id: 5,
      label: 'Share',
      onClick: () => { setSelectedPrompt(rowData); setIsShareModalOpen(true); },
    },
  ];

  const headers = [
    { value: 'name',        label: 'Name' },
    { value: 'description', label: 'Description' },
    // { value: 'category',    label: 'Category' },
    { value: 'llmModel',    label: 'Agents Using This Prompt' },
    { value: 'active',      label: 'Last Updated' },
  ];

  const getPromptStatus = (prompt) => {
    // TEMPORAL: Mock different statuses for demo 
    // This uses real data but simulates different tag states
    const promptId = prompt.uuid || prompt.id || '';
    const lastChar = promptId.slice(-1);
    
    if (['1', '4', '7', 'a', 'd'].includes(lastChar)) {
      return { label: 'Active', type: 'success' };
    }
    if (['2', '5', '8', 'b', 'e'].includes(lastChar)) {
      return { label: 'Draft', type: 'informative' };
    }
    
    return { label: 'Archive', type: 'error' };
    
    // TODO: Remove this mock logic and use real status when ready
    // Original logic:
    // if (prompt.archived || prompt.status === 'archived') {
    //   return { label: 'Archive', type: 'secondary' };
    // }
    // if (prompt.status === 'draft' || prompt.isDraft) {
    //   return { label: 'Draft', type: 'warning' };
    // }
    // if (prompt.active || prompt.status === 'active') {
    //   return { label: 'Active', type: 'success' };
    // }
    // return { label: 'Inactive', type: 'error' };
  };

  const showAlert = (alertObj) => {
    setAlert(alertObj);
    setTimeout(() => setAlert(null), 4000);
  };

  const handleArchivePrompt = () => {
    if (promptToArchive) {
      showAlert({
        title: 'Your prompt was archived successfully',
        message: 'You can restore it from your archive anytime.',
        severity: 'success',
      });
    }
  };

  const handleDuplicatePrompt = async () => {
    if (promptIdToDuplicate) {
      try {
        
        const originalPrompt = await getPromptById({ 
          id: promptIdToDuplicate, 
          tenantId 
        });
        
        if (originalPrompt && originalPrompt.data) {
          const promptData = originalPrompt.data;
          
          
          const newPromptData = {
            tenantId,
            name: `${promptData.name} Copy`,
            description: promptData.description,
            categories: promptData.categories || ["string"],
            tags: promptData.tags || ["string"],
            llmModel: promptData.llmModel || 'gpt-4-turbo',
            promptText: promptData.promptText,
            isPublic: false,
            scope: "internal"
          };
          
          
          await createPrompt(newPromptData);
          
          showAlert({
            title: 'Your Prompt has been successfully duplicated',
            message: 'You can modify if you wish.',
            severity: 'success',
          });
          
          
          fetchPrompts(currentPage, itemsPerPage);
        }
      } catch (error) {
        showAlert({
          title: 'Error duplicating prompt',
          message: 'There was an issue duplicating your prompt. Please try again.',
          severity: 'error',
        });
      }
    }
  };

  const fetchPrompts = useCallback(async (page, itemsPerPage) => {
    setLoading(true);
    try {
      const res = await listPrompts({
        tenantId,
        page,
        pageSize: itemsPerPage,
      });
      setPrompts(res.data || []);
      setMeta(res.meta);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPrompts(currentPage, itemsPerPage); }, [currentPage, itemsPerPage, fetchPrompts]);
  
  const tableData = useMemo(() => {
    return prompts.map((p) => ({
      ...p,
      name: mode === 'card'
          ? (p.name || '-')
          : (
              <NameWrapper>
                <div onClick={() => { setIsSlideoutOpen(!isSlideoutOpen); }}>
                  <AnnIcon name={"visibility"} size="small"/>
                </div>
                <span onClick={() => navigate(`/v2/pms/config/${p.uuid}`)}>
                  {p.name}
                </span>
              </NameWrapper>
          ),
      cardTitle: p.name || '-',
      active: p.active ? '-' : '-',
      llmModel: p.llmModel ? '-' : '-',
      // category: p.category || '-',
      element: (
        <div style={{maxWidth: '26px'}}>
          <Dropdown
            items={getMenuItems(p)}
            size="small"
            type="secondary"
          />
        </div>
      ),
    }));
  }, [prompts, mode, navigate, setIsSlideoutOpen]);

  const showTable = !loading && !error && tableData.length > 0;

  return (
    <>
      {alert && (
        <Alert
          title={alert.title}
          description={alert.message}
          severity={alert.severity}
          width={"692px"}
          top={"61px"}
          right={"16px"}
          onClose={() => setAlert(null)}
        />
      )}
      <PmsMainPageContainer>
        <Content>
          <PreviewSlideOut
            open={isSlideoutOpen}
            onClose={() => {
              setIsSlideoutOpen(false);
              setSelectedPromptForSlideout(null);
            }}
            selectedPrompt={selectedPromptForSlideout}
            onDuplicateClick={(prompt) => {
              setPromptToDuplicate(prompt);
              setPromptIdToDuplicate(prompt.uuid);
              setIsDuplicateModalOpen(true);
              setIsSlideoutOpen(false);
              setSelectedPromptForSlideout(null);
            }}
          />
          <Breadcrumb />
          <Header />

          <FilterBar mode={mode} setMode={setMode} />

          <TableContainer>
            {loading && <LoaderWrapper><CircularProgress /></LoaderWrapper >} 
            {error    && <div>Error loading prompts</div>}
            {showTable && (
              <Table
                headers={headers}
                data={tableData}
                mode={mode}
                gridColumns={3}
                gridNode={(item) => (
                  <CardViewX size="medium" >
                    <CardViewX.Header
                      avatarSrc="https://i.pravatar.cc/40"
                      menu={
                        <Dropdown items={getMenuItems(item)} size="small" type="secondary" />
                      }
                      tag={getPromptStatus(item)}
                      title={item.cardTitle}
                    />
                    <CardViewX.Description>{item.description}</CardViewX.Description>
                    <CardViewX.Meta>
                      <CardViewX.MetaItem
                        icon="person"
                        label="Creador"
                      />
                      <CardViewX.MetaItem
                        icon="lastUpdated"
                        label="A week ago"
                      />
                      <CardViewX.MetaItem
                        icon="group"
                        label="+7"
                      />
                    </CardViewX.Meta>
                                      
                    <CardViewX.Actions>
                      <CardViewX.SecondaryAction  
                        label="Preview"  
                        onClick={() => { 
                          const originalPrompt = prompts.find(p => p.uuid === item.uuid);
                          setSelectedPromptForSlideout(originalPrompt);
                          setIsSlideoutOpen(!isSlideoutOpen); 
                        }} 
                      />

                      <CardViewX.PrimaryAction label="Edit" onClick={() => navigate(`/v2/pms/config/${item.uuid}`)} />
                      
                    </CardViewX.Actions>
                  </CardViewX>
                )}
              />
            )}

            {!loading && !error && tableData.length === 0 && (
              <NoDataWrapper>
                <Icon name="noDataAvailable" width="201px" height="195px" />
                <NoDataTitle>No prompts yet</NoDataTitle>
                <NoDataDescription>
                  Click <strong>&quot;New&quot;</strong> to create your first prompt!
                </NoDataDescription>
              </NoDataWrapper>
            )}
          </TableContainer>
        </Content>
      </PmsMainPageContainer>

      {meta && (
        <Footer
          currentPage={currentPage}
          totalPages={meta.totalPages}
          itemsPerPage={meta.perPage}
          totalItems={meta.totalItems}
          onPageChange={setCurrentPage}
          disabled={loading}
          onItemsPerPageChange={setItemsPerPage}
        />
      )}
      
      {selectedPrompt && (
        <PromptShareModal
          isOpen={isShareModalOpen}
          onClose={() => { setIsShareModalOpen(false); setSelectedPrompt(null); }}
          vaultId={selectedPrompt.uuid}
          vaultName={selectedPrompt.name}
        />
      )}
      {promptToArchive && (
        <PromptArchiveModal
          isOpen={isArchiveModalOpen}
          onClose={() => {
            setIsArchiveModalOpen(false);
            setPromptToArchive(null);
          }}
          onArchive={handleArchivePrompt}
          promptName={promptToArchive.name}
        />
      )}
      {promptToDuplicate && (
        <PromptDuplicateModal
          isOpen={isDuplicateModalOpen}
          onClose={() => {
            setIsDuplicateModalOpen(false);
            setPromptToDuplicate(null);
            setPromptIdToDuplicate(null);
          }}
          onDuplicate={handleDuplicatePrompt}
          promptName={promptToDuplicate.name}
          promptId={promptIdToDuplicate}
        />
      )}
    </>
  );
};
export default PmsMainPage;